/**
 * Course schema types (DESIGN.md §3). Units: mm. Origin: top-left.
 */

/** Draw order: first = back (drawn first). Default 2nd cut is back; fairway/green etc. on top. */
export const SURFACE_KEYS = [
  'secondCut',
  'firstCut',
  'fairway',
  'green',
  'fringe',
  'bunker',
  'waste',
  'water',
  'cartPath',
  'outOfBounds',
  'hazard',
] as const;

export type SurfaceKey = (typeof SURFACE_KEYS)[number];

export type Point = [number, number];

export interface SurfaceShape {
  type: 'polygon' | 'path';
  points: Point[];
  /** 1–18; which hole this shape belongs to (cart path still stored per-hole, generator merges to Hole98) */
  holeNumber?: number;
}

export type Surfaces = Record<SurfaceKey, SurfaceShape[]>;

export interface Tee {
  id: string;
  holeNumber: number;
  label: string;
  points: Point[];
}

export interface Tree {
  x: number;
  y: number;
  type: string;
  radiusMm: number;
  /** 1–18; which hole this tree belongs to */
  holeNumber?: number;
}

export interface House {
  x: number;
  y: number;
  radiusMm: number;
  /** 1–18; which hole this house belongs to */
  holeNumber?: number;
}

/** Pin day: one pin per day per hole (Thu/Fri/Sat/Sun). */
export type PinDay = 'thu' | 'fri' | 'sat' | 'sun';

/** Pin placement on a green (hole position). */
export interface Pin {
  x: number;
  y: number;
  holeNumber: number;
  /** Which day this pin is for; at most one per day per hole. */
  day: PinDay;
}

/** Tee position color options (tee marker colors). */
export type TeePositionColorKey = 'red' | 'gold' | 'white' | 'gray' | 'blue' | 'black';

/** Tee-off position (must be inside a tee box). */
export interface TeePosition {
  x: number;
  y: number;
  holeNumber: number;
  /** Marker color; default 'white' if omitted. */
  color?: TeePositionColorKey;
}

/** 150-yard marker in the fairway. */
export interface Marker150 {
  x: number;
  y: number;
  holeNumber: number;
}

export interface ElevationGrid {
  cols: number;
  rows: number;
  cellSizeMm: number;
  values: number[];
}

export interface HoleAssignment {
  holeNumber: number;
  greenId?: string;
  teeIds: string[];
}

export type Par = 3 | 4 | 5;

export interface CourseMetadata {
  name: string;
  /** Allowed hole counts for builder navigation/UI. */
  holeCount: 9 | 18;
  widthMm: number;
  heightMm: number;
  createdAt: string;
  updatedAt: string;
  /** Hole display names; key = hole number 1–18. Default "Hole {n}" if absent. */
  holeNames?: Record<number, string>;
  /** Par per hole; key = hole number 1–18. Default 4 if absent. Affects grid size and disables Fairway/150 yd for par 3. */
  holePars?: Record<number, Par>;
}

export interface Course {
  version: number;
  metadata: CourseMetadata;
  surfaces: Surfaces;
  tees: Tee[];
  trees: Tree[];
  houses: House[];
  pins: Pin[];
  teePositions: TeePosition[];
  markers150: Marker150[];
  elevation: { grid: ElevationGrid };
  holes: HoleAssignment[];
}

/** Tool identifiers for the toolbar. Fringe is not a tool; it is drawn as 1 yd around each green. */
export type ToolId =
  | 'fairway'
  | 'firstCut'
  | 'secondCut'
  | 'green'
  | 'bunker'
  | 'waste'
  | 'water'
  | 'cartPath'
  | 'outOfBounds'
  | 'hazard'
  | 'house'
  | 'tee'
  | 'tree'
  | 'pin'
  | 'teePosition'
  | 'marker150'
  | 'elevation'
  | 'eraser'
  | 'pointer'
  | 'move'
  | 'reshape'
  | 'duplicate'
  | 'measure';

/** Hit/selection result for an item in the 2D view. */
export type SelectionHit =
  | { type: 'tree'; index: number }
  | { type: 'house'; index: number }
  | { type: 'tee'; index: number }
  | { type: 'surface'; key: SurfaceKey; index: number }
  | { type: 'pin'; index: number }
  | { type: 'teePosition'; index: number }
  | { type: 'marker150'; index: number };

/** Surface keys that have a drawing tool (fringe is drawn automatically around greens). */
export type SurfaceToolKey = Exclude<SurfaceKey, 'fringe'>;

export function isSurfaceTool(id: ToolId): id is SurfaceToolKey {
  return (SURFACE_KEYS as readonly string[]).includes(id) && (id as string) !== 'fringe';
}

export function isBrushTool(id: ToolId): boolean {
  return [
    'fairway',
    'firstCut',
    'secondCut',
    'green',
    'bunker',
    'waste',
    'water',
    'cartPath',
    'outOfBounds',
  ].includes(id);
}

/** Tools that draw a stroke (path) instead of a filled polygon: hazard, cart path. */
export function isPenTool(id: ToolId): boolean {
  return id === 'hazard' || id === 'cartPath';
}
